// context/AuthContext.js
import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword, 
  signInWithPopup,
  signOut, 
  onAuthStateChanged,
  updateProfile,
  sendPasswordResetEmail
} from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { auth, db, googleProvider } from '../firebase';
import { detectAndSetUserCurrency } from '../utils/currency';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Create user profile in Firestore
  const createUserProfile = async (user, additionalData = {}) => {
    if (!user) return;

    const userRef = doc(db, 'users', user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      const { displayName, email, photoURL } = user;
      const createdAt = new Date();

      try {
        await setDoc(userRef, {
          displayName: displayName || additionalData.name || '',
          email,
          photoURL: photoURL || null,
          createdAt,
          authMethod: additionalData.authMethod || 'email',
          ...additionalData
        });

        // Detect and set user currency based on location (non-blocking)
        detectAndSetUserCurrency(user.uid, db, doc, setDoc).catch(error => {
          console.error('Currency detection failed (non-critical):', error);
        });
      } catch (error) {
        console.error('Error creating user profile:', error);
        throw error;
      }
    }

    return userRef;
  };

  // Sign up with email and password
  const signup = async (email, password, name = '') => {
    console.log('🔧 AuthContext signup called with:', { email, name });
    setError(null);
    setSuccess(null);
    
    try {
      console.log('📡 Calling createUserWithEmailAndPassword...');
      const { user } = await createUserWithEmailAndPassword(auth, email, password);
      
      // Update the user's display name
      if (name) {
        await updateProfile(user, { displayName: name });
      }
      
      // Create user profile in Firestore
      await createUserProfile(user, { name, authMethod: 'email' });
      
      const successMessage = `Welcome, ${name || 'there'}! Your account has been created successfully.`;
      console.log('✅ Signup successful:', successMessage);
      setSuccess(successMessage);
      
      return user;
    } catch (error) {
      console.error('💥 Signup error in AuthContext:', error);
      
      let errorMessage = error.message;
      
      switch (error.code) {
        case 'auth/email-already-in-use':
          errorMessage = 'An account with this email already exists. Try signing in instead.';
          break;
        case 'auth/weak-password':
          errorMessage = 'Password should be at least 6 characters long.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Email/password accounts are not enabled. Please contact support.';
          break;
        default:
          errorMessage = 'Failed to create account. Please try again.';
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  // Sign in with email and password
  const signin = async (email, password) => {
    setError(null);
    setSuccess(null);
    
    try {
      const { user } = await signInWithEmailAndPassword(auth, email, password);
      return user;
    } catch (error) {
      let errorMessage = error.message;
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/wrong-password':
          errorMessage = 'Incorrect password for this email address.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This account has been disabled. Please contact support.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many failed login attempts. Please try again later or reset your password.';
          break;
        case 'auth/invalid-credential':
          errorMessage = 'Invalid email or password combination. Please check your credentials.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection and try again.';
          break;
        default:
          if (error.message.includes('auth/')) {
            errorMessage = 'Incorrect email or password combination.';
          } else {
            errorMessage = 'Failed to sign in. Please check your credentials.';
          }
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  // NEW: Sign in with Google
  const signInWithGoogle = async () => {
    setError(null);
    setSuccess(null);
    
    try {
      console.log('🔧 Starting Google sign-in...');
      const result = await signInWithPopup(auth, googleProvider);
      const user = result.user;
      
      console.log('✅ Google sign-in successful:', user.displayName);
      
      // Create user profile in Firestore (will only create if doesn't exist)
      await createUserProfile(user, { authMethod: 'google' });
      
      const successMessage = `Welcome back, ${user.displayName}!`;
      setSuccess(successMessage);
      
      return user;
    } catch (error) {
      console.error('💥 Google sign-in error:', error);
      
      let errorMessage = error.message;
      
      switch (error.code) {
        case 'auth/popup-closed-by-user':
          errorMessage = 'Sign-in was cancelled. Please try again.';
          break;
        case 'auth/popup-blocked':
          errorMessage = 'Pop-up was blocked by your browser. Please allow pop-ups and try again.';
          break;
        case 'auth/cancelled-popup-request':
          errorMessage = 'Only one sign-in popup is allowed at a time.';
          break;
        case 'auth/operation-not-allowed':
          errorMessage = 'Google sign-in is not enabled. Please contact support.';
          break;
        case 'auth/invalid-api-key':
          errorMessage = 'Invalid API configuration. Please contact support.';
          break;
        case 'auth/network-request-failed':
          errorMessage = 'Network error. Please check your connection and try again.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many requests. Please try again later.';
          break;
        case 'auth/user-disabled':
          errorMessage = 'This Google account has been disabled. Please contact support.';
          break;
        case 'auth/account-exists-with-different-credential':
          errorMessage = 'An account already exists with this email using a different sign-in method. Please try signing in with email/password.';
          break;
        default:
          errorMessage = 'Failed to sign in with Google. Please try again.';
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  // Reset password
  const resetPassword = async (email) => {
    setError(null);
    setSuccess(null);
    
    try {
      await sendPasswordResetEmail(auth, email);
      setSuccess('Password reset email sent! Check your inbox.');
      return true;
    } catch (error) {
      let errorMessage = error.message;
      
      switch (error.code) {
        case 'auth/user-not-found':
          errorMessage = 'No account found with this email address.';
          break;
        case 'auth/invalid-email':
          errorMessage = 'Please enter a valid email address.';
          break;
        case 'auth/too-many-requests':
          errorMessage = 'Too many reset attempts. Please try again later.';
          break;
        default:
          errorMessage = 'Failed to send reset email. Please try again.';
      }
      
      setError(errorMessage);
      throw error;
    }
  };

  // Sign out
  const logout = async () => {
    setError(null);
    try {
      await signOut(auth);
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  // Clear error and success
  const clearError = () => {
    setError(null);
    setSuccess(null);
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      // Unblock the app immediately — profile enrichment runs in background
      setUser(user ?? null);
      setLoading(false);

      if (!user) return;

      // Background: create profile if first sign-in + re-detect currency if needed
      (async () => {
        try {
          await createUserProfile(user);

          const userRef = doc(db, 'users', user.uid);
          const userSnap = await getDoc(userRef);
          if (userSnap.exists()) {
            const userData = userSnap.data();
            if (userData.preferredCurrency && !userData.detectedCountry) {
              detectAndSetUserCurrency(user.uid, db, doc, setDoc).catch(() => {});
            }
          }
        } catch (error) {
          console.error('Error in background user profile setup:', error);
        }
      })();
    });

    return unsubscribe;
  }, []);

  const value = {
    user,
    loading,
    error,
    success,
    signup,
    signin,
    signInWithGoogle, // NEW: Export Google sign-in function
    logout,
    resetPassword,
    clearError
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};